/**
 * EditDebriefingGuide Component
 * ORCA360 - Debriefing Preparation Report Editor
 *
 * Allows instructors to review and edit AI-generated debriefing guides
 * Following the ORCA360 CBTA Debriefing Coach specification (Parts A-D)
 *
 * Features:
 * - View/edit Parts A-D with accordion sections
 * - Part D marked as "Instructor Only - Not Shared"
 * - Auto-save with debouncing
 * - Audit logging for compliance
 * - PDF export with ORCA360 branding
 * - Transcript viewer (modal overlay)
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { createVersion } from '../lib/assessment-versions';
import { useAuditLog } from '../hooks/useAuditLog';
import { useAutoSave } from '../hooks/useAutoSave';
import { VersionHistoryDrawer } from '../components/VersionHistoryDrawer';
import { format } from 'date-fns';
import TranscriptViewer from '../components/TranscriptViewer';
import DebriefingExportModal from '../components/DebriefingExportModal';
import { DebriefingGuideContainer } from '../components/debriefing';
import EventTimeline from '../components/EventTimeline';
import { splitTimePhase } from '../lib/debriefing-time';
import type {
  DebriefingGuideRow,
  PartA_SessionIdentification,
  PartB_DebriefingFramework,
  PartC_SupportingMaterial,
  PartD_InstructorNotes,
  EventAnalysis,
  TEMEventType,
  UASRootCausePreAnalysis,
  BarrierBreakdownTEM,
} from '../types/debriefing-guide';

interface SessionInfo {
  trainee_name: string;
  instructor_name: string;
  session_date: string;
  aircraft_type: string;
  flight_phase: string;
  instructor_notes: string | null;
}

// TEM Event type colors
const EVENT_TYPE_COLORS: Record<TEMEventType, string> = {
  Threat: 'bg-amber-100 text-amber-800 border-amber-300',
  Error: 'bg-red-100 text-red-800 border-red-300',
  UAS: 'bg-purple-100 text-purple-800 border-purple-300',
  Resilience: 'bg-green-100 text-green-800 border-green-300',
};

export default function EditDebriefingGuide() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  
  // State
  const [guide, setGuide] = useState<DebriefingGuideRow | null>(null);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // View state
  const [guideView, setGuideView] = useState<'short' | 'full'>('short');

  // Accordion states
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    partA: true,
    partB: true,
    partC: false,
    partD: false,
  });
  
  // Transcript modal state
  const [transcriptIds, setTranscriptIds] = useState<string[]>([]);
  const [activeTimestamp, setActiveTimestamp] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  // Edit mode — guide loads read-only; Edit button enables editing
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);


  // Snapshot of guide at last save (for Cancel)
  const [savedGuide, setSavedGuide] = useState<DebriefingGuideRow | null>(null);

  const { logAction } = useAuditLog();

  // Auto-save setup
  const autoSave = useAutoSave({
    data: guide,
    onSave: async (data: DebriefingGuideRow | null) => {
      if (!data || !sessionId) return;

      const { error: updateError } = await supabase
        .from('debriefing_guides')
        .update({
          part_a: data.part_a,
          part_b: data.part_b,
          part_c: data.part_c,
          part_d: data.part_d,
          status: 'edited',
        })
        .eq('id', data.id);

      if (updateError) throw updateError;
    },
    enabled: guide !== null && isEditMode,
  });

  // Version history drawer
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const handleVersionRestore = useCallback(
    async (snapshot: Record<string, unknown>, fromVersionNumber: number) => {
      if (!guide) return;
      const nextVersion = (guide.version ?? 1) + 1;

      const { error } = await supabase
        .from('debriefing_guides')
        .update({
          part_a: snapshot.part_a,
          part_b: snapshot.part_b,
          part_c: snapshot.part_c,
          part_d: snapshot.part_d,
          version: nextVersion,
        })
        .eq('id', guide.id);
      if (error) throw error;

      const { data: { user } } = await supabase.auth.getUser();
      await createVersion(
        guide.id,
        'debriefing_guide',
        { ...snapshot, version: nextVersion },
        user?.id ?? null,
        { isAiBaseline: false, changeSummary: `Restored from version ${fromVersionNumber}` },
      );

      await logAction({
        action: 'debriefing_guide_updated',
        entity_type: 'debriefing_guide',
        entity_id: guide.id,
        metadata: { session_id: sessionId, restored_from_version: fromVersionNumber },
      });

      const restored: DebriefingGuideRow = {
        ...guide,
        part_a: snapshot.part_a as typeof guide.part_a,
        part_b: snapshot.part_b as typeof guide.part_b,
        part_c: snapshot.part_c as typeof guide.part_c,
        part_d: snapshot.part_d as typeof guide.part_d,
        version: nextVersion,
      };
      setGuide(restored);
      setSavedGuide(restored);
    },
    [guide, sessionId, logAction],
  );

  // Explicit save handler
  const handleSave = useCallback(async () => {
    if (!guide || !sessionId) return;
    setIsSaving(true);
    try {
      const nextVersion = (guide.version ?? 1) + 1;

      const { error: updateError } = await supabase
        .from('debriefing_guides')
        .update({
          part_a: guide.part_a,
          part_b: guide.part_b,
          part_c: guide.part_c,
          part_d: guide.part_d,
          status: 'edited',
          version: nextVersion,
        })
        .eq('id', guide.id);

      if (updateError) throw updateError;

      // Audit log — track which parts changed
      const changes: Record<string, { old: unknown; new: unknown }> = {};
      if (savedGuide) {
        if (JSON.stringify(guide.part_a) !== JSON.stringify(savedGuide.part_a))
          changes.part_a = { old: savedGuide.part_a, new: guide.part_a };
        if (JSON.stringify(guide.part_b) !== JSON.stringify(savedGuide.part_b))
          changes.part_b = { old: savedGuide.part_b, new: guide.part_b };
        if (JSON.stringify(guide.part_c) !== JSON.stringify(savedGuide.part_c))
          changes.part_c = { old: savedGuide.part_c, new: guide.part_c };
        if (JSON.stringify(guide.part_d) !== JSON.stringify(savedGuide.part_d))
          changes.part_d = { old: savedGuide.part_d, new: guide.part_d };
      }
      if (Object.keys(changes).length > 0) {
        await logAction({
          action: 'debriefing_guide_updated',
          entity_type: 'debriefing_guide',
          entity_id: guide.id,
          changes,
          metadata: { session_id: sessionId },
        });
      }

      // Version snapshot — only for guides with an AI baseline (skip legacy)
      const { data: existingVersions } = await supabase
        .from('assessment_versions')
        .select('id')
        .eq('assessment_id', guide.id)
        .eq('assessment_type', 'debriefing_guide')
        .limit(1);

      if (existingVersions && existingVersions.length > 0) {
        const { data: { user } } = await supabase.auth.getUser();
        await createVersion(
          guide.id,
          'debriefing_guide',
          { ...guide, version: nextVersion } as Record<string, unknown>,
          user?.id ?? null,
          { isAiBaseline: false }
        );
      }

      const saved = { ...guide, version: nextVersion, status: 'edited' as const };
      setGuide(saved);
      setSavedGuide(saved);
      setIsEditMode(false);
    } catch (err) {
      console.error('[EditDebriefingGuide] Save error:', err);
      alert('Failed to save debriefing guide');
    } finally {
      setIsSaving(false);
    }
  }, [guide, sessionId, savedGuide, logAction]);

  const handleEdit = useCallback(() => setIsEditMode(true), []);

  const handleCancel = useCallback(() => {
    if (savedGuide) setGuide(savedGuide);
    setIsEditMode(false);
  }, [savedGuide]);

  // Load debriefing guide
  const loadGuide = useCallback(async () => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    try {
      // Load session info
      const { data: session, error: sessionError } = await supabase
        .from('sessions')
        .select(`
          session_date,
          aircraft_type,
          flight_phase,
          instructor_notes,
          trainee:trainee_id(full_name),
          instructor:instructor_id(full_name)
        `)
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;
      if (!session) {
        setError('Session not found');
        return;
      }

      const traineeData = Array.isArray(session.trainee) ? session.trainee[0] : session.trainee;
      const instructorData = Array.isArray(session.instructor) ? session.instructor[0] : session.instructor;

      setSessionInfo({
        trainee_name: traineeData?.full_name || 'Unknown',
        instructor_name: instructorData?.full_name || 'Unknown',
        session_date: session.session_date || '',
        aircraft_type: session.aircraft_type || 'Unknown',
        flight_phase: session.flight_phase || 'Unknown',
        instructor_notes: session.instructor_notes || null,
      });

      // Load debriefing guide
      // Org isolation is transitive: session loaded via RLS (org-scoped),
      // so debriefing_guides filtered by that session_id are already org-isolated.
      const { data: guideData, error: guideError } = await supabase
        .from('debriefing_guides')
        .select('*')
        .eq('session_id', sessionId)
        .order('version', { ascending: false })
        .limit(1)
        .single();

      if (guideError) {
        if (guideError.code === 'PGRST116') {
          setError('No debriefing guide found for this session. Generate one first.');
        } else {
          throw guideError;
        }
        return;
      }

      setGuide(guideData);
      setSavedGuide(guideData);

      // Load transcript IDs for viewer
      const { data: transcripts } = await supabase
        .from('transcripts')
        .select('id')
        .eq('session_id', sessionId);

      if (transcripts) {
        setTranscriptIds(transcripts.map((t) => t.id));
      }


    } catch (err) {
      console.error('[EditDebriefingGuide] Error loading:', err);
      setError(err instanceof Error ? err.message : 'Failed to load debriefing guide');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadGuide();
  }, [loadGuide]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Unsaved changes guard on browser close/refresh
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isEditMode) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isEditMode]);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Handle part changes (local state only — committed on Save)
  const handlePartUpdate = useCallback(
    (part: 'partA' | 'partB' | 'partC' | 'partD', value: unknown) => {
      if (!guide || !isEditMode) return;
      const updated = {
        ...guide,
        [part === 'partA' ? 'part_a' : part === 'partB' ? 'part_b' : part === 'partC' ? 'part_c' : 'part_d']: value,
      };
      setGuide(updated);
    },
    [guide, isEditMode]
  );

  // Navigate to timestamp in transcript
  const handleTimestampClick = (timestamp: string) => {
    setActiveTimestamp(timestamp);
    setShowTranscript(true);
    // Clear after 5 seconds
    setTimeout(() => setActiveTimestamp(null), 5000);
  };

  // Finalize the guide
  const handleFinalize = async () => {
    if (!guide) return;
    if (isEditMode) {
      alert('Save or cancel your current edits before finalizing.');
      return;
    }
    const confirmed = window.confirm(
      'Are you sure you want to finalize this debriefing guide? This marks it as ready for the debrief session.'
    );
    
    if (!confirmed) return;
    
    try {
      const { error: updateError } = await supabase
        .from('debriefing_guides')
        .update({
          status: 'finalized',
          finalized_at: new Date().toISOString(),
        })
        .eq('id', guide.id);
      
      if (updateError) throw updateError;

      await logAction({
        action: 'debriefing_guide_finalized',
        entity_type: 'debriefing_guide',
        entity_id: guide.id,
        metadata: { session_id: sessionId },
      });

      setGuide({ ...guide, status: 'finalized' });
      alert('Debriefing guide finalized successfully!');
    } catch (err) {
      console.error('[EditDebriefingGuide] Finalize error:', err);
      alert('Failed to finalize debriefing guide');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orca-main"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!guide) {
    return null;
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-ice">
      {/* Compact Navigation Bar */}
      <div className="bg-navy px-4 py-2 flex items-center justify-between shrink-0 shadow-sm">
        <button
          onClick={() => navigate(`/sessions/${sessionId}`)}
          className="text-white hover:text-sky-200 text-sm font-medium flex items-center gap-2"
        >
          ← Back to Session
        </button>
        <span className="text-white/70 text-sm">
          {sessionInfo?.trainee_name} • {sessionInfo?.aircraft_type} • {sessionInfo?.session_date ? format(new Date(sessionInfo.session_date), 'PPP') : ''}
        </span>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
          {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-orca-main mb-2">
                Debriefing Preparation Report{guideView === 'short' && (
                  <span className="text-lg font-normal text-steel ml-2">(short guide)</span>
                )}
              </h1>
              <p className="text-steel text-sm">
                Session: {sessionInfo?.trainee_name} • {sessionInfo?.aircraft_type} •{' '}
                {sessionInfo?.session_date
                  ? format(new Date(sessionInfo.session_date), 'PPP')
                  : 'Unknown date'}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Edit / Save / Cancel */}
              {guide.status !== 'finalized' && (
                !isEditMode ? (
                  <button
                    onClick={handleEdit}
                    className="px-4 py-2 bg-orca-elevate text-white rounded hover:opacity-90 text-sm font-semibold"
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-semibold disabled:opacity-50"
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isSaving}
                      className="px-4 py-2 bg-white border border-steel/30 text-navy rounded hover:bg-ice text-sm font-semibold disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </>
                )
              )}
              
              {/* Status Badge */}
              <span
                className={`text-sm px-3 py-1 rounded-full ${
                  guide.status === 'finalized'
                    ? 'bg-green-100 text-green-700'
                    : guide.status === 'edited'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {guide.status.charAt(0).toUpperCase() + guide.status.slice(1)}
              </span>
              
              {/* Save Status */}
              {autoSave.isSaving && (
                <span className="text-sm px-3 py-1 rounded-full bg-amber-100 text-amber-700">
                  ⟳ Saving...
                </span>
              )}
              {!autoSave.isSaving && autoSave.hasUnsavedChanges && (
                <span className="text-sm px-3 py-1 rounded-full bg-blue-100 text-blue-700">
                  • Unsaved changes
                </span>
              )}
              {!autoSave.isSaving && !autoSave.hasUnsavedChanges && (
                <span className="text-sm px-3 py-1 rounded-full bg-green-100 text-green-700">
                  ✓ All changes saved
                </span>
              )}

              {/* Version History */}
              <button
                onClick={() => setShowVersionHistory(true)}
                className="px-4 py-2 bg-white border border-steel/30 text-navy rounded hover:bg-ice text-sm font-semibold"
              >
                History
              </button>

              {/* Toggle Transcript */}
              <button
                onClick={() => {
                  setActiveTimestamp(null);
                  setShowTranscript(!showTranscript);
                }}
                className="px-4 py-2 bg-orca-elevate text-white rounded hover:opacity-90 text-sm"
              >
                {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
              </button>
              
              {/* Export Button */}
              <button
                onClick={() => setShowExportModal(true)}
                className="px-4 py-2 bg-orca-vision text-white rounded hover:opacity-90 text-sm"
              >
                Export Guide
              </button>
              
              {/* Finalize Button */}
              {guide.status !== 'finalized' && (
                <button
                  onClick={handleFinalize}
                  className="px-4 py-2 bg-orca-main text-white rounded hover:opacity-90 text-sm"
                >
                  Finalize Guide
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Event Timeline */}
        {sessionId && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <h2 className="text-sm font-semibold text-navy mb-3">Session Timeline</h2>
            <EventTimeline
              sessionId={sessionId}
              onEventClick={(ts) => {
                setActiveTimestamp(ts);
                setShowTranscript(true);
              }}
            />
          </div>
        )}

        {/* Short Guide (default) / Full Guide toggle container */}
        <DebriefingGuideContainer
          guide={guide}
          onGuideChange={setGuide}
          readOnly={!isEditMode || guide.status === 'finalized'}
          sessionId={sessionId!}
          instructorNotes={sessionInfo?.instructor_notes}
          onViewChange={setGuideView}
          renderFullGuide={() => (
            <>
              {/* Part A: Session Identification */}
              <SectionAccordion
                title="Part A — Session Identification"
                expanded={expandedSections.partA ?? false}
                onToggle={() => toggleSection('partA')}
              >
                <PartASection
                  data={guide.part_a}
                  onChange={(value) => handlePartUpdate('partA', value)}
                />
              </SectionAccordion>

              {/* Part B: Debriefing Framework */}
              <SectionAccordion
                title="Part B — Debriefing Framework (ORCA360)"
                expanded={expandedSections.partB ?? false}
                onToggle={() => toggleSection('partB')}
                badge={`${guide.part_b.eventFocusedExploration?.length || 0} Events`}
              >
                <PartBSection
                  data={guide.part_b}
                  onChange={(value) => handlePartUpdate('partB', value)}
                  onTimestampClick={handleTimestampClick}
                />
              </SectionAccordion>

              {/* Part D: Supporting Material */}
              <SectionAccordion
                title="Part D — Supporting Material (Instructor Use)"
                expanded={expandedSections.partC ?? false}
                onToggle={() => toggleSection('partC')}
                badge={`${guide.part_c.observedEventsTable?.length || 0} Events`}
              >
                <PartCSection
                  data={guide.part_c}
                  onChange={(value) => handlePartUpdate('partC', value)}
                />
              </SectionAccordion>

              {/* Part E: Instructor Notes */}
              <SectionAccordion
                title="Part E — Instructor Notes (Not Shared)"
                expanded={expandedSections.partD ?? false}
                onToggle={() => toggleSection('partD')}
                badge="🔒 Confidential"
                badgeColor="bg-red-100 text-red-700"
              >
                <PartDSection
                  data={guide.part_d}
                  onChange={(value) => handlePartUpdate('partD', value)}
                />
              </SectionAccordion>

              {/* Invocation Line */}
              <div className="bg-orca-main/10 rounded-lg p-6 mt-6 text-center">
                <p className="text-orca-main font-medium italic">
                  &ldquo;{guide.invocation_line}&rdquo;
                </p>
              </div>

              {/* Metadata Footer */}
              <div className="text-xs text-steel mt-4 text-center">
                Generated: {guide.metadata?.generatedAt ? format(new Date(guide.metadata.generatedAt), 'PPpp') : 'Unknown'} &bull;
                Model: {guide.metadata?.model || 'Unknown'} &bull;
                Events: {guide.metadata?.eventsDetected || 0} &bull;
                UAS: {guide.metadata?.uasDetected || 0}
              </div>
            </>
          )}
        />
      </div>

      {/* Transcript Modal */}
      {showTranscript && transcriptIds.length > 0 && (
        <TranscriptViewer
          transcriptIds={transcriptIds}
          sessionId={sessionId}
          onClose={() => setShowTranscript(false)}
          highlightTimestamp={activeTimestamp ?? undefined}
          sessionInfo={{
            trainee_name: sessionInfo?.trainee_name,
            session_date: sessionInfo?.session_date,
          }}
        />
      )}

      {/* Export Modal */}
      {sessionId && (
        <DebriefingExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          sessionId={sessionId}
        />
      )}

      {/* Version History Drawer */}
      <VersionHistoryDrawer
        assessmentId={guide?.id}
        assessmentType="debriefing_guide"
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        onRestore={handleVersionRestore}
      />
    </div>
  );
}

// ============================================================================
// SECTION ACCORDION COMPONENT
// ============================================================================

interface SectionAccordionProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  badge?: string;
  badgeColor?: string;
  children: React.ReactNode;
}

function SectionAccordion({
  title,
  expanded,
  onToggle,
  badge,
  badgeColor = 'bg-orca-elevate/10 text-orca-elevate',
  children,
}: SectionAccordionProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm mb-4 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg font-semibold text-orca-main">{title}</span>
          {badge && (
            <span className={`text-xs px-2 py-1 rounded-full ${badgeColor}`}>
              {badge}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-steel transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && <div className="p-4 pt-0 border-t border-gray-100">{children}</div>}
    </div>
  );
}

// ============================================================================
// PART A SECTION
// ============================================================================

interface PartASectionProps {
  data: PartA_SessionIdentification;
  onChange: (value: PartA_SessionIdentification) => void;
}

function PartASection({ data }: PartASectionProps) {
  return (
    <div className="grid grid-cols-2 gap-4 mt-4">
      <InfoField label="Airline" value={data.airline} />
      <InfoField label="Aircraft Type" value={data.aircraftType} />
      <InfoField label="Session Name" value={data.sessionName} />
      <InfoField label="Date / Time" value={data.dateTime} />
      
      <div className="col-span-2">
        <h4 className="text-sm font-medium text-steel mb-2">Participants</h4>
        <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded">
          <InfoField label="Instructor" value={data.participants.instructor} />
          <InfoField label="Captain" value={data.participants.captain} />
          <InfoField label="First Officer" value={data.participants.firstOfficer} />
        </div>
      </div>
      
      <div className="col-span-2">
        <InfoField label="PF/PM Assignments" value={data.participants.pfPmAssignments} />
      </div>
      
      {data.roleAttributionNotes && (
        <div className="col-span-2 bg-amber-50 p-3 rounded text-sm text-amber-800">
          <strong>Role Attribution Notes:</strong> {data.roleAttributionNotes}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PART B SECTION
// ============================================================================

interface PartBSectionProps {
  data: PartB_DebriefingFramework;
  onChange: (value: PartB_DebriefingFramework) => void;
  onTimestampClick: (timestamp: string) => void;
}

function PartBSection({ data, onTimestampClick }: PartBSectionProps) {
  return (
    <div className="space-y-6 mt-4">
      {/* Framework Statement */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">Framework Statement</h4>
        <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded">{data.frameworkStatement}</p>
      </div>
      
      {/* Part C: Opening Self-Assessment */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">Part C — Opening Self-Assessment Prompts</h4>
        <ul className="list-disc list-inside space-y-1 bg-blue-50 p-4 rounded">
          {data.openingSelfAssessment?.prompts?.map((prompt, idx) => (
            <li key={idx} className="text-sm text-blue-800">"{prompt}"</li>
          ))}
        </ul>
        <p className="text-xs text-steel mt-2 italic">
          Note: These questions should be asked but not answered by the instructor.
        </p>
      </div>
      
      {/* UAS-Focused RCA */}
      {data.uasFocusedRCA && data.uasFocusedRCA.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-steel mb-2">UAS-Focused Root Cause Analysis</h4>
          {data.uasFocusedRCA.map((uas, idx) => (
            <div key={idx} className="bg-purple-50 p-4 rounded mb-3 border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded">
                  UAS {idx + 1}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${
                  uas.severity === 'Serious' ? 'bg-red-100 text-red-700' :
                  uas.severity === 'Moderate' ? 'bg-amber-100 text-amber-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {uas.severity}
                </span>
              </div>
              <p className="font-medium text-purple-900 mb-2">{uas.uasDescription}</p>
              
              {/* Question Ladder */}
              <div className="mb-3">
                <span className="text-xs text-steel">Discovery Questions:</span>
                <ol className="list-decimal list-inside text-sm mt-1 space-y-1">
                  {uas.questionLadder.map((q, qIdx) => (
                    <li key={qIdx} className="text-purple-800">{q}</li>
                  ))}
                </ol>
              </div>
              
              {/* Instructor RCA Summary */}
              <div className="bg-red-50 border border-red-200 p-3 rounded text-sm">
                <span className="text-xs font-medium text-red-600">
                  ⚠️ Instructor Preparation Only – Not to be stated as conclusions
                </span>
                <p className="text-red-800 mt-1">{uas.instructorRCASummary}</p>
              </div>
              
              {/* Competency Classification */}
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-steel">Primary Competency:</span>
                  <p className="text-sm font-medium">{uas.primaryCompetency.code} - {uas.primaryCompetency.name}</p>
                </div>
                <div>
                  <span className="text-xs text-steel">Secondary:</span>
                  <p className="text-sm">
                    {uas.secondaryCompetencies.map(c => c.code).join(', ') || 'None'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Event-Focused Exploration */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">Event-Focused Exploration</h4>
        {data.eventFocusedExploration?.map((event, idx) => (
          <EventCard key={idx} event={event} onTimestampClick={onTimestampClick} />
        ))}
      </div>
      
      {/* Crew-Defined Strategy */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">Crew-Defined Future Strategy</h4>
        <div className="bg-green-50 p-4 rounded border border-green-200">
          <p className="text-green-900 font-medium">"{data.crewDefinedStrategy?.statement}"</p>
          
          {data.crewDefinedStrategy?.qualityCheck && (
            <div className="mt-3 flex gap-2 flex-wrap">
              <QualityBadge label="Behavioral" checked={data.crewDefinedStrategy.qualityCheck.behavioral} />
              <QualityBadge label="Transferable" checked={data.crewDefinedStrategy.qualityCheck.transferable} />
              <QualityBadge label="Competency-Linked" checked={data.crewDefinedStrategy.qualityCheck.competencyLinked} />
              <QualityBadge label="Crew-Owned" checked={data.crewDefinedStrategy.qualityCheck.crewOwned} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PART C SECTION
// ============================================================================

interface PartCSectionProps {
  data: PartC_SupportingMaterial;
  onChange: (value: PartC_SupportingMaterial) => void;
}

function PartCSection({ data }: PartCSectionProps) {
  return (
    <div className="space-y-6 mt-4">
      {/* Observed Events Table */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">Observed Events Table</h4>
        <table className="w-full text-sm border border-gray-200 rounded overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left w-12">#</th>
              <th className="px-3 py-2 text-left w-32">Time / Phase</th>
              <th className="px-3 py-2 text-left">What Happened (Facts Only)</th>
            </tr>
          </thead>
          <tbody>
            {data.observedEventsTable?.map((event) => (
              <tr key={event.eventNumber} className="border-t border-gray-100">
                <td className="px-3 py-2 font-medium">{event.eventNumber}</td>
                <td className="px-3 py-2 text-steel">{event.timePhase}</td>
                <td className="px-3 py-2">{event.whatHappened}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* UAS Root Cause Pre-Analysis */}
      {(() => {
        // Normalize legacy single-object to array format
        const normalizeUASArray = (uasData: unknown): UASRootCausePreAnalysis[] => {
          if (!uasData) return [];
          if (Array.isArray(uasData)) return uasData;
          return [uasData as UASRootCausePreAnalysis]; // Wrap single object in array
        };
        const uasAnalysisList = normalizeUASArray(data.uasRootCausePreAnalysis);

        if (uasAnalysisList.length === 0) return null;

        return (
          <div>
            <h4 className="text-sm font-medium text-steel mb-2">UAS Root Cause Pre-Analysis</h4>
            {uasAnalysisList.map((rca, rcaIdx) => (
              <div key={rcaIdx} className="bg-purple-50 p-4 rounded border border-purple-200 mb-3">
                <p className="text-sm mb-3">
                  <strong>UAS:</strong>{' '}
                  {rca.uasDescription || <em className="text-gray-500">[What aircraft state deviated from desired / safe state]</em>}
                </p>

                {/* Barrier Breakdown - always render as TEM table */}
                <div className="mb-3">
                  <strong className="text-sm">Barrier Breakdown (TEM):</strong>
                  {(() => {
                    // Always render as table - handle both string and structured formats
                    const isStructured = typeof rca.barrierBreakdown !== 'string' && rca.barrierBreakdown !== null;
                    const barrierData = isStructured ? rca.barrierBreakdown as BarrierBreakdownTEM : null;

                    return (
                      <table className="w-full text-sm border-collapse mt-2">
                        <thead>
                          <tr className="bg-sky-100">
                            <th className="border border-gray-300 p-2 text-left font-medium">Layer</th>
                            <th className="border border-gray-300 p-2 text-left font-medium w-20">Present?</th>
                            <th className="border border-gray-300 p-2 text-left font-medium">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-300 p-2">Threat identified early</td>
                            <td className="border border-gray-300 p-2 text-center">
                              {barrierData?.threatIdentifiedEarly?.present === true ? 'Yes' :
                               barrierData?.threatIdentifiedEarly?.present === false ? 'No' : ''}
                            </td>
                            <td className="border border-gray-300 p-2">{barrierData?.threatIdentifiedEarly?.notes || ''}</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">Error detected</td>
                            <td className="border border-gray-300 p-2 text-center">
                              {barrierData?.errorDetected?.present === true ? 'Yes' :
                               barrierData?.errorDetected?.present === false ? 'No' : ''}
                            </td>
                            <td className="border border-gray-300 p-2">{barrierData?.errorDetected?.notes || ''}</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">Error trapped</td>
                            <td className="border border-gray-300 p-2 text-center">
                              {barrierData?.errorTrapped?.present === true ? 'Yes' :
                               barrierData?.errorTrapped?.present === false ? 'No' : ''}
                            </td>
                            <td className="border border-gray-300 p-2">{barrierData?.errorTrapped?.notes || ''}</td>
                          </tr>
                          <tr>
                            <td className="border border-gray-300 p-2">Recovery action effective</td>
                            <td className="border border-gray-300 p-2 text-center">
                              {barrierData?.recoveryActionEffective?.present === true ? 'Yes' :
                               barrierData?.recoveryActionEffective?.present === false ? 'No' : ''}
                            </td>
                            <td className="border border-gray-300 p-2">{barrierData?.recoveryActionEffective?.notes || ''}</td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })()}
                </div>

                <div className="mb-3">
                  <strong className="text-sm">Preliminary RCA (5-Why Logic - Instructor Only):</strong>
                  <ol className="list-decimal list-inside text-sm mt-1 space-y-1">
                    {(rca.fiveWhyLogic || []).map((why: string, idx: number) => (
                      <li key={idx}>{why}</li>
                    ))}
                  </ol>
                </div>

                <p className="text-sm"><strong>Underlying Competency:</strong> {rca.underlyingCompetency}</p>
              </div>
            ))}
          </div>
        );
      })()}
      
      {/* Event Classification Blocks */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">Event Classification Blocks</h4>
        <div className="grid grid-cols-2 gap-3">
          {data.eventClassificationBlocks?.map((block) => (
            <div key={block.eventNumber} className="bg-gray-50 p-3 rounded border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium">Event {block.eventNumber}</span>
                {block.temClassification && (
                  <span className={`text-xs px-2 py-0.5 rounded ${EVENT_TYPE_COLORS[block.temClassification] || 'bg-gray-200'}`}>
                    {block.temClassification}
                  </span>
                )}
              </div>
              <p className="text-xs text-steel">Primary: {block.primaryCompetency}</p>
              <p className="text-xs text-steel">Secondary: {(block.secondaryCompetencies || []).join(', ') || 'None'}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PART D SECTION
// ============================================================================

interface PartDSectionProps {
  data: PartD_InstructorNotes;
  onChange: (value: PartD_InstructorNotes) => void;
}

function PartDSection({ data }: PartDSectionProps) {
  return (
    <div className="space-y-6 mt-4">
      <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-800">
        ⚠️ <strong>Confidential</strong>: These notes are for instructor self-reflection only and should not be shared with trainees.
      </div>
      
      {/* Calibration Reflections */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">Calibration Reflections</h4>
        <p className="text-sm bg-gray-50 p-4 rounded">{data.calibrationReflections}</p>
      </div>
      
      {/* Facilitation Improvement Points */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">Facilitation Improvement Points</h4>
        <p className="text-sm bg-gray-50 p-4 rounded">{data.facilitationImprovementPoints}</p>
      </div>
      
      {/* IEC Classifications */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">IEC Self-Reflection Guidance</h4>
        <div className="space-y-3">
          {data.iecClassifications?.map((iec, idx) => (
            <div key={idx} className="bg-indigo-50 p-3 rounded border border-indigo-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="bg-indigo-600 text-white text-xs px-2 py-0.5 rounded">{iec.iec}</span>
                <span className="text-sm font-medium text-indigo-900">{iec.iecName}</span>
              </div>
              <p className="text-sm text-indigo-800">{iec.reflection}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* ORCA Alignment Observations */}
      <div>
        <h4 className="text-sm font-medium text-steel mb-2">ORCA Alignment Observations</h4>
        <p className="text-sm bg-gray-50 p-4 rounded">{data.orcaAlignmentObservations}</p>
      </div>
    </div>
  );
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function InfoField({ label, value }: { label: string; value: string }) {
  const isNotStated = value === 'Not stated in transcript';
  return (
    <div>
      <span className="text-xs text-steel">{label}</span>
      <p className={`text-sm ${isNotStated ? 'text-gray-400 italic' : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  );
}

function EventCard({
  event,
  onTimestampClick,
}: {
  event: EventAnalysis;
  onTimestampClick: (timestamp: string) => void;
}) {
  const { timestamp, description } = splitTimePhase(event.timePhase);

  return (
    <div className="bg-gray-50 p-4 rounded mb-3 border border-gray-200">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="bg-orca-main text-white text-xs px-2 py-1 rounded">
          Event {event.eventNumber}
        </span>
        <span className={`text-xs px-2 py-1 rounded border ${EVENT_TYPE_COLORS[event.eventType]}`}>
          {event.eventType}
        </span>
        {timestamp ? (
          <span className="text-xs text-steel inline-flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onTimestampClick(timestamp)}
              className="font-mono text-orca-elevate hover:underline"
            >
              {timestamp}
            </button>
            {description && <span>{description}</span>}
          </span>
        ) : (
          <span className="text-xs text-steel">{event.timePhase}</span>
        )}
      </div>
      
      <p className="text-sm mb-3">{event.factsSummary}</p>
      
      {/* Facilitator Questions */}
      <div className="mb-3">
        <span className="text-xs text-steel">Facilitator Questions:</span>
        <ul className="list-disc list-inside text-sm mt-1 space-y-1">
          {event.facilitatorQuestions.map((q, idx) => (
            <li key={idx} className="text-blue-700">"{q}"</li>
          ))}
        </ul>
      </div>
      
      {/* Competency Classification */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="text-steel">Primary:</span>
          <p className="font-medium">{event.primaryCompetency.code} - {event.primaryCompetency.name}</p>
          <p className="text-steel">{event.primaryCompetency.behavioralDescription}</p>
        </div>
        <div>
          <span className="text-steel">Secondary:</span>
          <p className="font-medium">
            {event.secondaryCompetencies.map(c => c.code).join(', ') || 'None'}
          </p>
        </div>
      </div>
      
      {/* Countermeasures */}
      {event.countermeasures.length > 0 && (
        <div className="mt-3">
          <span className="text-xs text-steel">Countermeasures:</span>
          <ul className="list-disc list-inside text-sm mt-1">
            {event.countermeasures.map((cm, idx) => (
              <li key={idx} className="text-green-700">{cm}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function QualityBadge({ label, checked }: { label: string; checked: boolean }) {
  return (
    <span
      className={`text-xs px-2 py-1 rounded ${
        checked
          ? 'bg-green-100 text-green-700 border border-green-300'
          : 'bg-gray-100 text-gray-400 border border-gray-200'
      }`}
    >
      {checked ? '✓' : '○'} {label}
    </span>
  );
}

